import { MapGrouping } from './map-grouping';
import { Layers, MapLayer } from './map-layer';
import { ZoneObject } from '@ng-cloud/badger-core/map/objects/zone-object';
import { NavZone } from '@ng-cloud/badger-core/models/nav-zone';
import * as _ from 'lodash';

@Layers([
  { name: 'navZones', zIndex: 6 }
])
export class NavZoneGrouping extends MapGrouping<NavZone, ZoneObject> {
  private readonly DEFAULT_FONT_SIZE = 14;

  readonly modeColors = {
    [NavZone.KEEP_OUT_MODE]: '#be3232',
    [NavZone.KEEP_IN_MODE]: '#307930',
    [NavZone.SPEED_MODE]: '#eda54c',
  };

  private readonly SPEED_BASELINE = .3;
  private readonly SPEED_MIN = 0.1;
  private readonly SPEED_MAX = 1.5;

  private readonly SPEED_SLOW_COLOR = '#0744a5'; // blue-ish anchor
  private readonly SPEED_FAST_COLOR = '#f6ff40'; // amber-ish anchor
  private readonly SPEED_NEUTRAL    = '#d9ffcd'; // near-baseline anchor
  private readonly FAST_GAMMA = .10;
  private readonly SLOW_GAMMA = .10;
  private readonly SPEED_ZONE_FILL_OPACITY = 0.8;

  private clamp(n: number, a: number, b: number) { return Math.max(a, Math.min(b, n)); }
  private hexToRgb(hex: string) {
    const h = hex.replace('#','');
    const full = h.length === 3 ? h.split('').map(c => c+c).join('') : h;
    const num = parseInt(full, 16);
    return { r: (num>>16)&255, g: (num>>8)&255, b: num&255 };
  }
  private rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
    const to = (x:number)=>Math.round(this.clamp(x,0,255)).toString(16).padStart(2,'0');
    return `#${to(r)}${to(g)}${to(b)}`;
  }
  private mixHex(aHex: string, bHex: string, t: number) {
    const a = this.hexToRgb(aHex), b = this.hexToRgb(bHex);
    const tt = this.clamp(t, 0, 1);
    const lerp = (x:number,y:number)=>x + (y - x) * tt;
    return this.rgbToHex({ r: lerp(a.r,b.r), g: lerp(a.g,b.g), b: lerp(a.b,b.b) });
  }
  private speedToColor(speed?: number): string {
    const base = this.SPEED_BASELINE, min = this.SPEED_MIN, max = this.SPEED_MAX;
    const slow = this.SPEED_SLOW_COLOR, fast = this.SPEED_FAST_COLOR, neutral = this.SPEED_NEUTRAL;
    const s = this.clamp(speed ?? base, min, max);

    // slight hint of neutral even at baseline (optional)
    // if (s === base) return this.mixHex(neutral, '#d1d5db', 0.25);

    if (s < base) {
      const t = (base - s) / (base - min);
      const eased = Math.pow(t, this.SLOW_GAMMA);
      return this.mixHex(neutral, slow, eased);
    } else {
      const t = (s - base) / (max - base);
      const eased = Math.pow(t, this.FAST_GAMMA);
      return this.mixHex(neutral, fast, eased);
    }
  }

  private prevStaleCheck: number[] = [];

  constructor(public options: any = {}, public layerName: string = 'navZones', public zIndex: number = 1) {
    super(options);

    this.layers = [new MapLayer(Object.assign({}, { name: layerName, zIndex: zIndex}, { grouping: this }))];
  }

  createObject(data: NavZone): ZoneObject {
    const points = data.points.map(pt => this.storeMap.pointToPixels(pt));
    const opts = Object.assign(
      { speedBaseline: this.SPEED_BASELINE },
      this.getColor(data),
      this.options
    );
    return new ZoneObject(data, points, opts);
  }

  protected getFontSize(): number {
    return Math.max(Math.ceil(this.storeMap.width / 150), this.DEFAULT_FONT_SIZE);
  }

  isStale(navZoneIDs: number[]): boolean {
    if (!navZoneIDs) {
      return false;
    }
    const sortedIds = navZoneIDs.sort();
    const stale = !_.isEqual(sortedIds, this.prevStaleCheck);
    this.prevStaleCheck = sortedIds;
    return stale;
  }

  protected getColor(navZone: NavZone) {
    if (navZone.mode === NavZone.SPEED_MODE) {
      const fill = this.speedToColor(navZone.speed_mps); // ← per-zone solid color from speed
      return {
        polygonOptions: {
          fill,
          opacity: this.SPEED_ZONE_FILL_OPACITY,          // minimally translucent to the map
          stroke: '#222',         // outline for accessibility
          strokeWidth: 2,
          strokeUniform: true as any
        },
        // Keep the existing pill color or switch to 'fill' if you prefer
        labelOptions: { textBackgroundColor: this.modeColors[navZone.mode], fontSize: this.getFontSize() }
      };
    }

    // Non-speed zones unchanged
    return {
      polygonOptions: { fill: this.modeColors[navZone.mode] },
      labelOptions:   { textBackgroundColor: this.modeColors[navZone.mode], fontSize: this.getFontSize() }
    };
  }
}
